import type { AuthType, BodyMode, HttpMethod, RawFormat } from '@core/entities/ApiRequest';

export type CodeGenLanguage =
  | 'curl'
  | 'javascript-fetch'
  | 'javascript-axios'
  | 'nodejs-axios'
  | 'php-curl'
  | 'php-laravel'
  | 'python-requests'
  | 'java-okhttp'
  | 'go-http'
  | 'csharp-httpclient'
  | 'ruby-net-http';

export const CODE_GEN_LANGUAGES: { label: string; value: CodeGenLanguage; highlight: string }[] = [
  { label: 'cURL', value: 'curl', highlight: 'shell' },
  { label: 'JavaScript — Fetch', value: 'javascript-fetch', highlight: 'javascript' },
  { label: 'JavaScript — Axios', value: 'javascript-axios', highlight: 'javascript' },
  { label: 'Node.js — Axios', value: 'nodejs-axios', highlight: 'javascript' },
  { label: 'PHP — cURL', value: 'php-curl', highlight: 'php' },
  { label: 'PHP — Laravel HTTP client', value: 'php-laravel', highlight: 'php' },
  { label: 'Python — Requests', value: 'python-requests', highlight: 'python' },
  { label: 'Java — OkHttp', value: 'java-okhttp', highlight: 'java' },
  { label: 'Go — net/http', value: 'go-http', highlight: 'go' },
  { label: 'C# — HttpClient', value: 'csharp-httpclient', highlight: 'csharp' },
  { label: 'Ruby — Net::HTTP', value: 'ruby-net-http', highlight: 'ruby' },
];

export interface CodeGenParam {
  type: 'query' | 'path';
  key: string;
  value: string;
  enabled: boolean;
}

export interface CodeGenHeader {
  key: string;
  value: string;
  enabled: boolean;
}

export interface CodeGenBodyField {
  key: string;
  value: string;
  type: 'text' | 'file';
  enabled: boolean;
}

export interface CodeGenBody {
  mode: BodyMode;
  rawContent?: string | null;
  rawFormat?: RawFormat | null;
  fields?: CodeGenBodyField[];
  graphqlQuery?: string | null;
  graphqlVariables?: Record<string, unknown> | null;
}

export interface CodeGenRequest {
  method: HttpMethod;
  url: string;
  params: CodeGenParam[];
  headers: CodeGenHeader[];
  authType: AuthType;
  authConfig: Record<string, unknown> | null;
  body: CodeGenBody | null;
}

interface ResolvedRequest {
  url: string;
  headers: Array<{ key: string; value: string }>;
  bodyKind: 'none' | 'raw' | 'form' | 'multipart' | 'graphql' | 'binary';
  raw: string | null;
  form: Array<{ key: string; value: string; type: 'text' | 'file' }>;
  authComment: string | null;
}

const b64 = (value: string): string => btoa(value);

const applyPathParams = (url: string, params: CodeGenParam[]): string =>
  params
    .filter((param) => param.type === 'path' && param.enabled && param.key)
    .reduce((acc, param) => acc.split(`:${param.key}`).join(param.value), url);

const appendQueryParams = (url: string, pairs: Array<{ key: string; value: string }>): string => {
  if (pairs.length === 0) return url;
  const query = pairs
    .map((pair) => `${encodeURIComponent(pair.key)}=${encodeURIComponent(pair.value)}`)
    .join('&');
  return url.includes('?') ? `${url}&${query}` : `${url}?${query}`;
};

const rawContentType: Record<RawFormat, string> = {
  json: 'application/json',
  xml: 'application/xml',
  html: 'text/html',
  javascript: 'application/javascript',
  text: 'text/plain',
};

const AUTH_NOT_REPRESENTABLE: Partial<Record<AuthType, string>> = {
  digest:
    "Digest auth is negotiated with the server (a 401 challenge/response) and can't be pre-computed into a static snippet.",
  aws_signature:
    'AWS Signature v4 is computed per-request (it signs the exact method/url/headers/body/timestamp) — not representable as a static header here.',
  jwt: 'The JWT is signed at send time; paste your own signed token as a Bearer header if you need a static snippet.',
  ntlm: 'NTLM is a multi-round handshake and has no static-header equivalent.',
};

const resolveRequest = (request: CodeGenRequest): ResolvedRequest => {
  let url = applyPathParams(request.url, request.params);
  const queryFromParams = request.params.filter(
    (param) => param.type === 'query' && param.enabled && param.key,
  );
  url = appendQueryParams(url, queryFromParams);

  const headers = request.headers
    .filter((header) => header.enabled && header.key)
    .map((header) => ({ key: header.key, value: header.value }));

  const config = request.authConfig ?? {};
  let authComment: string | null = null;
  const authQuery: Array<{ key: string; value: string }> = [];

  switch (request.authType) {
    case 'bearer':
      if (typeof config.token === 'string' && config.token)
        headers.push({ key: 'Authorization', value: `Bearer ${config.token}` });
      break;
    case 'basic':
      headers.push({
        key: 'Authorization',
        value: `Basic ${b64(`${String(config.username ?? '')}:${String(config.password ?? '')}`)}`,
      });
      break;
    case 'api_key':
      if (typeof config.key === 'string' && config.key) {
        if ((config.add_to ?? 'header') === 'query')
          authQuery.push({ key: config.key, value: String(config.value ?? '') });
        else headers.push({ key: config.key, value: String(config.value ?? '') });
      }
      break;
    case 'oauth2':
      if (typeof config.access_token === 'string' && config.access_token) {
        headers.push({ key: 'Authorization', value: `Bearer ${config.access_token}` });
      }
      break;
    default:
      authComment = AUTH_NOT_REPRESENTABLE[request.authType] ?? null;
  }

  url = appendQueryParams(url, authQuery);

  const body = request.body;
  if (!body || body.mode === 'none') {
    return { url, headers, bodyKind: 'none', raw: null, form: [], authComment };
  }

  if (body.mode === 'raw') {
    const contentType = rawContentType[body.rawFormat ?? 'text'];
    if (!headers.some((h) => h.key.toLowerCase() === 'content-type'))
      headers.push({ key: 'Content-Type', value: contentType });
    return { url, headers, bodyKind: 'raw', raw: body.rawContent ?? '', form: [], authComment };
  }

  if (body.mode === 'urlencoded') {
    const form = (body.fields ?? [])
      .filter((f) => f.enabled)
      .map((f) => ({ key: f.key, value: f.value, type: 'text' as const }));
    if (!headers.some((h) => h.key.toLowerCase() === 'content-type')) {
      headers.push({ key: 'Content-Type', value: 'application/x-www-form-urlencoded' });
    }
    return { url, headers, bodyKind: 'form', raw: null, form, authComment };
  }

  if (body.mode === 'multipart_form') {
    const form = (body.fields ?? [])
      .filter((f) => f.enabled)
      .map((f) => ({ key: f.key, value: f.value, type: f.type }));
    return { url, headers, bodyKind: 'multipart', raw: null, form, authComment };
  }

  if (body.mode === 'graphql') {
    const raw = JSON.stringify(
      { query: body.graphqlQuery ?? '', variables: body.graphqlVariables ?? {} },
      null,
      2,
    );
    if (!headers.some((h) => h.key.toLowerCase() === 'content-type'))
      headers.push({ key: 'Content-Type', value: 'application/json' });
    return { url, headers, bodyKind: 'graphql', raw, form: [], authComment };
  }

  return { url, headers, bodyKind: 'binary', raw: null, form: [], authComment };
};

const indent = (lines: string[], spaces = 2): string =>
  lines.map((line) => ' '.repeat(spaces) + line).join('\n');
const jsonString = (value: string): string => JSON.stringify(value);

const generateCurl = (r: ResolvedRequest, method: HttpMethod): string => {
  const parts = [`curl --request ${method} \\`, `  --url ${jsonString(r.url)} \\`];
  r.headers.forEach((h) => parts.push(`  --header ${jsonString(`${h.key}: ${h.value}`)} \\`));
  if (r.bodyKind === 'raw' || r.bodyKind === 'graphql') {
    parts.push(`  --data ${jsonString(r.raw ?? '')}`);
  } else if (r.bodyKind === 'form') {
    r.form.forEach((f) => parts.push(`  --data-urlencode ${jsonString(`${f.key}=${f.value}`)} \\`));
    parts[parts.length - 1] = parts[parts.length - 1].replace(/ \\$/, '');
  } else if (r.bodyKind === 'multipart') {
    r.form.forEach((f) =>
      parts.push(
        `  --form ${jsonString(f.type === 'file' ? `${f.key}=@${f.value}` : `${f.key}=${f.value}`)} \\`,
      ),
    );
    parts[parts.length - 1] = parts[parts.length - 1].replace(/ \\$/, '');
  } else {
    parts[parts.length - 1] = parts[parts.length - 1].replace(/ \\$/, '');
  }
  return [r.authComment ? `# ${r.authComment}` : null, ...parts].filter(Boolean).join('\n');
};

const generateFetch = (r: ResolvedRequest, method: HttpMethod): string => {
  const headerLines = r.headers.map((h) => `  ${jsonString(h.key)}: ${jsonString(h.value)},`);
  const bodyLine =
    r.bodyKind === 'raw' || r.bodyKind === 'graphql'
      ? `  body: ${jsonString(r.raw ?? '')},`
      : r.bodyKind === 'form'
        ? `  body: new URLSearchParams(${JSON.stringify(Object.fromEntries(r.form.map((f) => [f.key, f.value])))}).toString(),`
        : r.bodyKind === 'multipart'
          ? `  body: (() => { const fd = new FormData(); ${r.form.map((f) => (f.type === 'file' ? `/* fd.append(${jsonString(f.key)}, fileInput.files[0]) */` : `fd.append(${jsonString(f.key)}, ${jsonString(f.value)});`)).join(' ')} return fd; })(),`
          : null;

  return [
    r.authComment ? `// ${r.authComment}` : null,
    `const response = await fetch(${jsonString(r.url)}, {`,
    `  method: ${jsonString(method)},`,
    headerLines.length ? `  headers: {\n${indent(headerLines, 4)}\n  },` : null,
    bodyLine,
    `});`,
    ``,
    `const data = await response.json();`,
    `console.log(response.status, data);`,
  ]
    .filter((line) => line !== null)
    .join('\n');
};

const generateAxios = (r: ResolvedRequest, method: HttpMethod, isNode: boolean): string => {
  const headerLines = r.headers.map((h) => `    ${jsonString(h.key)}: ${jsonString(h.value)},`);
  const dataLine =
    r.bodyKind === 'raw' || r.bodyKind === 'graphql'
      ? `  data: ${r.raw && r.raw.trim().startsWith('{') ? r.raw : jsonString(r.raw ?? '')},`
      : r.bodyKind === 'form'
        ? `  data: new URLSearchParams(${JSON.stringify(Object.fromEntries(r.form.map((f) => [f.key, f.value])))}).toString(),`
        : null;

  return [
    isNode ? `const axios = require('axios');` : `import axios from 'axios';`,
    ``,
    r.authComment ? `// ${r.authComment}` : null,
    `const response = await axios({`,
    `  method: ${jsonString(method)},`,
    `  url: ${jsonString(r.url)},`,
    headerLines.length ? `  headers: {\n${indent(headerLines, 4)}\n  },` : null,
    dataLine,
    `});`,
    ``,
    `console.log(response.status, response.data);`,
  ]
    .filter((line) => line !== null)
    .join('\n');
};

const generatePhpCurl = (r: ResolvedRequest, method: HttpMethod): string => {
  const headerLines = r.headers.map((h) => `    ${jsonString(`${h.key}: ${h.value}`)},`);
  return [
    r.authComment ? `// ${r.authComment}` : null,
    `$ch = curl_init();`,
    ``,
    `curl_setopt_array($ch, [`,
    `    CURLOPT_URL => ${jsonString(r.url)},`,
    `    CURLOPT_RETURNTRANSFER => true,`,
    `    CURLOPT_CUSTOMREQUEST => ${jsonString(method)},`,
    headerLines.length ? `    CURLOPT_HTTPHEADER => [\n${indent(headerLines, 8)}\n    ],` : null,
    r.bodyKind === 'raw' || r.bodyKind === 'graphql'
      ? `    CURLOPT_POSTFIELDS => ${jsonString(r.raw ?? '')},`
      : null,
    r.bodyKind === 'form'
      ? `    CURLOPT_POSTFIELDS => http_build_query(${phpArray(r.form)}),`
      : null,
    `]);`,
    ``,
    `$response = curl_exec($ch);`,
    `curl_close($ch);`,
    ``,
    `echo $response;`,
  ]
    .filter((line) => line !== null)
    .join('\n');
};

const phpArray = (pairs: Array<{ key: string; value: string }>): string =>
  `[\n${pairs.map((p) => `    ${jsonString(p.key)} => ${jsonString(p.value)},`).join('\n')}\n]`;

const generatePhpLaravel = (r: ResolvedRequest, method: HttpMethod): string => {
  const headersArray = r.headers.length ? `->withHeaders(${phpArray(r.headers)})\n    ` : '';
  const methodCall = method.toLowerCase();
  const bodyArg =
    r.bodyKind === 'raw' || r.bodyKind === 'graphql'
      ? `json_decode(${jsonString(r.raw ?? '{}')}, true)`
      : r.bodyKind === 'form'
        ? phpArray(r.form)
        : '[]';

  return [
    r.authComment ? `// ${r.authComment}` : null,
    `use Illuminate\\Support\\Facades\\Http;`,
    ``,
    `$response = Http::${headersArray}${methodCall}(${jsonString(r.url)}${['post', 'put', 'patch'].includes(methodCall) && r.bodyKind !== 'none' ? `, ${bodyArg}` : ''});`,
    ``,
    `$response->json();`,
  ]
    .filter((line) => line !== null)
    .join('\n');
};

const generatePython = (r: ResolvedRequest, method: HttpMethod): string => {
  const pyDict = (pairs: Array<{ key: string; value: string }>) =>
    `{\n${pairs.map((p) => `    ${jsonString(p.key)}: ${jsonString(p.value)},`).join('\n')}\n}`;

  return [
    `import requests`,
    ``,
    r.authComment ? `# ${r.authComment}` : null,
    r.headers.length ? `headers = ${pyDict(r.headers)}` : null,
    r.bodyKind === 'form' ? `data = ${pyDict(r.form)}` : null,
    r.bodyKind === 'raw' || r.bodyKind === 'graphql'
      ? `payload = ${jsonString(r.raw ?? '')}`
      : null,
    ``,
    `response = requests.request(`,
    `    ${jsonString(method)},`,
    `    ${jsonString(r.url)},`,
    r.headers.length ? `    headers=headers,` : null,
    r.bodyKind === 'form' ? `    data=data,` : null,
    r.bodyKind === 'raw' || r.bodyKind === 'graphql' ? `    data=payload,` : null,
    `)`,
    ``,
    `print(response.status_code, response.text)`,
  ]
    .filter((line) => line !== null)
    .join('\n');
};

const generateJavaOkHttp = (r: ResolvedRequest, method: HttpMethod): string => {
  const bodyVar =
    r.bodyKind === 'raw' || r.bodyKind === 'graphql'
      ? `RequestBody body = RequestBody.create(${jsonString(r.raw ?? '')}, MediaType.parse(${jsonString(r.headers.find((h) => h.key.toLowerCase() === 'content-type')?.value ?? 'text/plain')}));`
      : r.bodyKind === 'form'
        ? `FormBody body = new FormBody.Builder()\n${indent(
            r.form.map((f) => `.add(${jsonString(f.key)}, ${jsonString(f.value)})`),
            4,
          )}\n    .build();`
        : null;

  const bodyArg = ['POST', 'PUT', 'PATCH'].includes(method)
    ? bodyVar
      ? 'body'
      : 'RequestBody.create(new byte[0], null)'
    : 'null';

  return [
    r.authComment ? `// ${r.authComment}` : null,
    `OkHttpClient client = new OkHttpClient();`,
    ``,
    bodyVar,
    ``,
    `Request request = new Request.Builder()`,
    `    .url(${jsonString(r.url)})`,
    `    .method(${jsonString(method)}, ${bodyArg})`,
    ...r.headers.map((h) => `    .addHeader(${jsonString(h.key)}, ${jsonString(h.value)})`),
    `    .build();`,
    ``,
    `Response response = client.newCall(request).execute();`,
    `System.out.println(response.body().string());`,
  ]
    .filter((line) => line !== null)
    .join('\n');
};

const generateGo = (r: ResolvedRequest, method: HttpMethod): string => {
  const bodyExpr =
    r.bodyKind === 'raw' || r.bodyKind === 'graphql'
      ? `strings.NewReader(${jsonString(r.raw ?? '')})`
      : r.bodyKind === 'form'
        ? `strings.NewReader(${jsonString(r.form.map((f) => `${encodeURIComponent(f.key)}=${encodeURIComponent(f.value)}`).join('&'))})`
        : 'nil';

  return [
    `package main`,
    ``,
    `import (`,
    `    "fmt"`,
    `    "io"`,
    `    "net/http"`,
    r.bodyKind === 'raw' || r.bodyKind === 'form' || r.bodyKind === 'graphql'
      ? `    "strings"`
      : null,
    `)`,
    ``,
    `func main() {`,
    r.authComment ? `    // ${r.authComment}` : null,
    `    req, _ := http.NewRequest(${jsonString(method)}, ${jsonString(r.url)}, ${bodyExpr})`,
    ...r.headers.map((h) => `    req.Header.Add(${jsonString(h.key)}, ${jsonString(h.value)})`),
    ``,
    `    client := &http.Client{}`,
    `    res, err := client.Do(req)`,
    `    if err != nil {`,
    `        panic(err)`,
    `    }`,
    `    defer res.Body.Close()`,
    ``,
    `    body, _ := io.ReadAll(res.Body)`,
    `    fmt.Println(res.StatusCode, string(body))`,
    `}`,
  ]
    .filter((line) => line !== null)
    .join('\n');
};

const generateCsharp = (r: ResolvedRequest, method: HttpMethod): string => {
  const methodMap: Partial<Record<HttpMethod, string>> = {
    GET: 'Get',
    POST: 'Post',
    PUT: 'Put',
    PATCH: 'Patch',
    DELETE: 'Delete',
  };
  const helper = methodMap[method];

  const bodyVar =
    r.bodyKind === 'raw' || r.bodyKind === 'graphql'
      ? `new StringContent(${jsonString(r.raw ?? '')}, Encoding.UTF8, ${jsonString(r.headers.find((h) => h.key.toLowerCase() === 'content-type')?.value ?? 'text/plain')})`
      : r.bodyKind === 'form'
        ? `new FormUrlEncodedContent(new Dictionary<string, string> {\n${indent(
            r.form.map((f) => `{ ${jsonString(f.key)}, ${jsonString(f.value)} },`),
            4,
          )}\n})`
        : null;

  const lines = [
    r.authComment ? `// ${r.authComment}` : null,
    `using var client = new HttpClient();`,
    ...r.headers
      .filter((h) => h.key.toLowerCase() !== 'content-type')
      .map(
        (h) => `client.DefaultRequestHeaders.Add(${jsonString(h.key)}, ${jsonString(h.value)});`,
      ),
  ];

  if (helper && (helper === 'Get' || helper === 'Delete')) {
    lines.push(`var response = await client.${helper}Async(${jsonString(r.url)});`);
  } else if (helper && bodyVar) {
    lines.push(
      `var content = ${bodyVar};`,
      `var response = await client.${helper}Async(${jsonString(r.url)}, content);`,
    );
  } else {
    lines.push(
      `var request = new HttpRequestMessage(new HttpMethod(${jsonString(method)}), ${jsonString(r.url)});`,
      bodyVar ? `request.Content = ${bodyVar};` : null,
      `var response = await client.SendAsync(request);`,
    );
  }

  lines.push(`var body = await response.Content.ReadAsStringAsync();`, `Console.WriteLine(body);`);

  return lines.filter((line) => line !== null).join('\n');
};

const generateRuby = (r: ResolvedRequest, method: HttpMethod): string => {
  const classMap: Partial<Record<HttpMethod, string>> = {
    GET: 'Get',
    POST: 'Post',
    PUT: 'Put',
    PATCH: 'Patch',
    DELETE: 'Delete',
  };

  return [
    `require 'net/http'`,
    `require 'uri'`,
    ``,
    r.authComment ? `# ${r.authComment}` : null,
    `uri = URI(${jsonString(r.url)})`,
    `http = Net::HTTP.new(uri.host, uri.port)`,
    `http.use_ssl = uri.scheme == "https"`,
    ``,
    `request = Net::HTTP::${classMap[method] ?? 'Get'}.new(uri)`,
    ...r.headers.map((h) => `request[${jsonString(h.key)}] = ${jsonString(h.value)}`),
    r.bodyKind === 'raw' || r.bodyKind === 'graphql'
      ? `request.body = ${jsonString(r.raw ?? '')}`
      : null,
    r.bodyKind === 'form'
      ? `request.set_form_data(${JSON.stringify(Object.fromEntries(r.form.map((f) => [f.key, f.value])))})`
      : null,
    ``,
    `response = http.request(request)`,
    `puts response.code, response.body`,
  ]
    .filter((line) => line !== null)
    .join('\n');
};

export const generateCode = (request: CodeGenRequest, language: CodeGenLanguage): string => {
  const resolved = resolveRequest(request);

  switch (language) {
    case 'curl':
      return generateCurl(resolved, request.method);
    case 'javascript-fetch':
      return generateFetch(resolved, request.method);
    case 'javascript-axios':
      return generateAxios(resolved, request.method, false);
    case 'nodejs-axios':
      return generateAxios(resolved, request.method, true);
    case 'php-curl':
      return generatePhpCurl(resolved, request.method);
    case 'php-laravel':
      return generatePhpLaravel(resolved, request.method);
    case 'python-requests':
      return generatePython(resolved, request.method);
    case 'java-okhttp':
      return generateJavaOkHttp(resolved, request.method);
    case 'go-http':
      return generateGo(resolved, request.method);
    case 'csharp-httpclient':
      return generateCsharp(resolved, request.method);
    case 'ruby-net-http':
      return generateRuby(resolved, request.method);
    default:
      return '';
  }
};
